import { ColumnDef } from '@tanstack/react-table';
import get from 'lodash/get';
import React from 'react';

export interface SortingRule {
    id: string;
    desc?: boolean;
}

export interface RowLike<T> {
    original: T;
    id: string;
    index: number;
    isExpanded: boolean;
    toggleRowExpanded(): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface CellProps<T = any, V = any> {
    value: V;
    row: RowLike<T>;
    cell: { value: V };
    column: { id: string };
}

export interface ReactColumn<T extends object = {}> {
    id?: string;
    Header?: React.ReactNode | (() => React.ReactNode);
    accessor?: keyof T | string | ((row: T) => unknown);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Cell?: (props: CellProps<T, any>) => React.ReactNode;
    disableSortBy?: boolean;
    width?: number | string;
    minWidth?: number;
    maxWidth?: number;
    style?: React.CSSProperties;
}

export type Column<T extends object = {}> = ReactColumn<T>;

export function columnId<T extends object>(
    column: ReactColumn<T>,
    index: number
) {
    if (column.id) {
        return column.id;
    }
    if (typeof column.accessor === 'string') {
        return column.accessor;
    }
    return `column_${index}`;
}

// v7 allowed an accessor to return JSX; only primitives may be stringified.
function renderAccessorValue(value: unknown): React.ReactNode {
    if (value === undefined || value === null) {
        return null;
    }
    if (React.isValidElement(value) || Array.isArray(value)) {
        return value as React.ReactNode;
    }
    if (typeof value === 'object') {
        return null;
    }
    return String(value);
}

export function toColumnDef<T extends object>(
    column: ReactColumn<T>,
    index: number
): ColumnDef<T> {
    const accessor = column.accessor;
    const accessorFn =
        typeof accessor === 'function'
            ? accessor
            : typeof accessor === 'string'
              ? (row: T) => get(row, accessor)
              : () => undefined;
    const id = columnId(column, index);
    return {
        id,
        accessorFn,
        enableSorting: !column.disableSortBy,
        header: () =>
            typeof column.Header === 'function'
                ? column.Header()
                : (column.Header ?? null),
        cell: (ctx) => {
            const value = ctx.getValue();
            if (!column.Cell) {
                return renderAccessorValue(value);
            }
            return column.Cell({
                value,
                cell: { value },
                column: { id },
                row: {
                    original: ctx.row.original,
                    id: ctx.row.id,
                    index: ctx.row.index,
                    isExpanded: ctx.row.getIsExpanded(),
                    toggleRowExpanded: () => ctx.row.toggleExpanded()
                }
            });
        },
        meta: {
            style: {
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                ...column.style
            }
        }
    };
}

declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData, TValue> {
        style?: React.CSSProperties;
    }
}
